import { z } from 'zod/v4';

import { ApprovalStatus, MonetaryValueBackendRuntype, QuantityRuntype } from '../backendTypes';
import { AvailabilityRuntype } from '../offer/availablityBackendTypes';
import { OfferResponseRuntype, OneTimeCostDTORuntype } from '../offer/offerBackendTypes';
import { PartAlternativeLiteRuntype, PartLite, PartLiteRuntype, PartOptionLiteRuntype } from '../part/partBackendTypes';
import {
    QuoteRequestLineItemAssignmentReason,
    QuoteRequestLineItemStatus,
} from '../quote-request/quoteRequestBackendTypes';
import {
    CostSummaryRuntype,
    OfferIdAndSignatureRuntype,
    SolutionStatusRuntype,
    TagRuntype,
} from '../solution/solutionBackendTypes';
import { PartAlternativeStrategyRuntype } from '../sourcingScenario/solutionPreferenceBackendTypes';

export const SolutionQuantitiesDtoRuntype = z.object({
    line_quantity: QuantityRuntype,
    total_purchase_quantity: QuantityRuntype,
    aggregated_quantity: QuantityRuntype,
    required_quantity: QuantityRuntype,
    scrap_quantity: QuantityRuntype,
    excess_quantity: QuantityRuntype.nullable(),
});
export type SolutionQuantitiesDtoDTO = z.infer<typeof SolutionQuantitiesDtoRuntype>;

const TotalCostOfOwnershipActionRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Relative'), value: z.string() }),
    z.object({ type: z.literal('Absolute'), value: MonetaryValueBackendRuntype }),
]);

const ManualTcoCostRuntype = z.object({
    name: z.string(),
    action: TotalCostOfOwnershipActionRuntype,
});

export type ManualTcoConfigurationDTO = z.infer<typeof ManualTcoConfigurationRuntype>;
const ManualTcoConfigurationRuntype = z.object({
    include_automatic_costs: z.boolean(),
    manual_costs: z.array(ManualTcoCostRuntype),
});

export const ManualTcoCostsRuntype = z.object({
    packaging: ManualTcoConfigurationRuntype.nullable(),
    discount: ManualTcoConfigurationRuntype.nullable(),
    shipping: ManualTcoConfigurationRuntype.nullable(),
    customs: ManualTcoConfigurationRuntype.nullable(),
    other: ManualTcoConfigurationRuntype.nullable(),
});

export type ManualAdditionalSolutionCostDTO = z.infer<typeof ManualTcoCostsRuntype>;

export const SourcingLineItemUpdateRequestRuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('ManualScrapQuantity'),
        data: z.null().or(z.number()),
    }),
    z.object({
        kind: z.literal('ManualCosts'),
        data: z.object({
            manual_one_time_costs: z.array(OneTimeCostDTORuntype).nullable(),
            manual_tco_costs: ManualTcoCostsRuntype.nullable(),
        }),
    }),
    z.object({
        kind: z.literal('Notes'),
        data: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('ManualSolutionStatus'),
        data: SolutionStatusRuntype.nullable(),
    }),
    z.object({
        kind: z.literal('ManualSourcedBy'),
        data: z.enum(['Internal', 'External']).nullable(),
    }),
    z.object({
        kind: z.literal('ManuallySelectedSolution'),
        data: OfferIdAndSignatureRuntype.nullable(),
    }),
    z.object({
        kind: z.literal('PartAlternativeStrategy'),
        data: PartAlternativeStrategyRuntype.nullable(),
    }),
]);
export type SourcingLineItemUpdateRequest = z.infer<typeof SourcingLineItemUpdateRequestRuntype>;

export interface BulkSourcingLineItemUpdate extends z.infer<typeof BulkSourcingLineItemUpdateRuntype> {}
export const BulkSourcingLineItemUpdateRuntype = z.object({
    items: z.array(
        z.object({
            id: z.string(),
            update: SourcingLineItemUpdateRequestRuntype,
        }),
    ),
});

export type OfferSelectionMetadataDTO = z.infer<typeof OfferSelectionMetadataRuntype>;
export const OfferSelectionUserRunType = z.object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
});
export const OfferSelectionMetadataRuntype = z.object({
    last_offer_selection_time: z.iso.datetime().nullable(),
    last_offer_selected_by: OfferSelectionUserRunType.nullable(),
});

/** Has no Pending: a pending line item shows in `line_item_status`. */
export enum SolutionSelection {
    Automatic = 'Automatic',
    Manual = 'Manual',
}
export const SolutionSelectionRuntype = z.enum(SolutionSelection);

export enum SourcingLineItemCalculationStatus {
    Pending = 'Pending',
    NoOffer = 'NoOffer',
    Sourced = 'Sourced',
}
export const SourcingLineItemCalculationStatusRuntype = z.enum(SourcingLineItemCalculationStatus);

export const SourcingLineItemStatusRuntype = z.object({
    original: SolutionStatusRuntype,
    manual: SolutionStatusRuntype.nullable(),
    effective: SolutionStatusRuntype,
});
export type SourcingLineItemStatusDTO = z.infer<typeof SourcingLineItemStatusRuntype>;

export const SolutionSummaryRuntype = z.object({
    status: SourcingLineItemStatusRuntype,
    manual_sourced_by: z.enum(['Internal', 'External']).nullable(),
    solution_tags: z.array(TagRuntype),
    notes: z.string().nullable(),
});
export type SolutionSummaryDTO = z.infer<typeof SolutionSummaryRuntype>;

export const BomSummaryRuntype = z.object({
    bom_ipns: z.array(z.string()),
    bom_cpns: z.array(z.string()),
    bom_notes: z.array(z.string()),
});
export type BomSummaryDTO = z.infer<typeof BomSummaryRuntype>;

export enum AbcClassificationLabel {
    A = 'A',
    B = 'B',
    C = 'C',
    Unknown = 'Unknown',
}
export const AbcClassificationLabelRuntype = z.enum(AbcClassificationLabel);

export const AbcClassificationReasonRuntype = z.union([
    z.object({ type: z.literal('CostPercentage'), data: z.number() }),
    z.object({ type: z.literal('NoOffer') }),
    z.object({ type: z.literal('Consigned') }),
]);
export type AbcClassificationReasonDTO = z.infer<typeof AbcClassificationReasonRuntype>;

export const AbcClassificationRuntype = z.object({
    label: AbcClassificationLabelRuntype,
    reason: AbcClassificationReasonRuntype,
});
export type AbcClassificationDTO = z.infer<typeof AbcClassificationRuntype>;

const AggregatedBomItemDataRuntype = z.object({
    assembly_id: z.string(),
    design_item_ids: z.array(z.string()),
});

const PartDemandOriginRuntype = z.object({
    demand_id: z.string(),
    context: z.string().nullable(),
    delivery_start_date: z.string().nullable(),
    delivery_end_date: z.string().nullable(),
});

export const LineItemOriginRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('AggregatedBomItems'),
        data: z.array(AggregatedBomItemDataRuntype),
    }),
    z.object({
        type: z.literal('PartDemand'),
        data: PartDemandOriginRuntype,
    }),
]);
export type LineItemOriginDTO = z.infer<typeof LineItemOriginRuntype>;

const QuoteRequestLineItemRuntype = z.object({
    request_part: PartLiteRuntype,
    quote_request_id: z.string(),
    quote_request_line_item_id: z.number(),
    assignment_reason: z.enum(QuoteRequestLineItemAssignmentReason),
    status: z.enum(QuoteRequestLineItemStatus),
});

/** Simpler than the offer's `OneTimeCostDTO`. */
const SourcingOneTimeCostRuntype = z.object({
    description: z.string().nullable(),
    price: MonetaryValueBackendRuntype,
});

const SiblingLineItemRuntype = z.object({
    sourcing_scenario_id: z.string(),
    solution_configuration_id: z.string(),
});

export type SourcingLineItemPartAlternativeStrategy = z.infer<typeof SourcingLineItemPartAlternativeStrategyRuntype>;
export const SourcingLineItemPartAlternativeStrategyRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Inherit') }),
    z.object({ type: z.literal('Override'), data: PartAlternativeStrategyRuntype }),
]);

// ---------------------------------------------------------------------------
// Award Status
//
// Whether the line item's selected offer matches the offer awarded in the
// demand group's award scenario. `Awarded` carries the award scenario's offer
// selection metadata and the id of its sourcing line item.
// ---------------------------------------------------------------------------
const AwardingInfoRuntype = z.object({
    offer_selection_metadata: OfferSelectionMetadataRuntype,
    award_sourcing_line_item_id: z.string(),
});

export const AwardStatusRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('NotAwarded') }),
    z.object({ type: z.literal('Awarded'), data: AwardingInfoRuntype }),
]);
export type AwardStatusDTO = z.infer<typeof AwardStatusRuntype>;

const SourcingLineItemBaseFields = {
    id: z.string(),
    sourcing_scenario_id: z.string(),
    quantities: SolutionQuantitiesDtoRuntype,
    manual_scrap_quantity: z.number().nullable(),
    origin: LineItemOriginRuntype,
    solution_summary: SolutionSummaryRuntype,
    bom_summary: BomSummaryRuntype,
    /**
     * All part options of the BOM item (approved, pending and rejected), each with its
     * approval status. Unique per part — no duplicates even if the same part appears
     * multiple times on the BOM item.
     */
    part_options: z.array(PartOptionLiteRuntype),
    /**
     * Flat list of all part alternatives offered in the context of this line item,
     * unique per (requested_part, offered_part) pair. The `requested_part` may match a
     * part option in `part_options` but does not have to (e.g. inner OTS parts of
     * generic/IPN part options, or alternatives of alternatives) — linking back is the
     * frontend's (best-effort) job.
     */
    part_alternatives: z.array(PartAlternativeLiteRuntype),
    part_category: z.number().nullable(),
    abc_classification: AbcClassificationRuntype,
    sibling_line_items: z.array(SiblingLineItemRuntype),
    quote_request_status: z.array(QuoteRequestLineItemRuntype),
    offer_selection_metadata: OfferSelectionMetadataRuntype,
    /**
     * Per-line-item override for the scenario's `part_alternative_strategy`.
     * `Inherit` falls back to the sourcing scenario; `Override` pins a concrete strategy.
     */
    part_alternative_strategy: SourcingLineItemPartAlternativeStrategyRuntype,
};

const ExternallySourcedRuntype = z.object({
    ...SourcingLineItemBaseFields,
    sourced_by: z.literal('External' as const),
    line_item_status: SourcingLineItemCalculationStatusRuntype,
    offer_id_and_signature: z.null(),
    selected_part: z.null(),
    selected_part_options: z.null(),
    cost_summary: z.null(),
    offer: z.null(),
    availability: z.null(),
    selection: z.null(),
    manual_one_time_costs: z.null(),
    manual_tco_costs: z.null(),
});

/** `line_item_status` is Pending or NoOffer. */
const InternallyNotSourcedRuntype = z.object({
    ...SourcingLineItemBaseFields,
    sourced_by: z.literal('Internal' as const),
    line_item_status: SourcingLineItemCalculationStatusRuntype,
    selected_part: PartLiteRuntype.nullable(),
    selected_part_options: z.array(PartLiteRuntype).nullable(),
    offer_id_and_signature: z.null(),
    cost_summary: z.null(),
    offer: z.null(),
    availability: z.null(),
    selection: z.null(),
    manual_one_time_costs: z.null(),
    manual_tco_costs: z.null(),
});

/** `line_item_status` is Sourced. */
const InternallySourcedRuntype = z.object({
    ...SourcingLineItemBaseFields,
    sourced_by: z.literal('Internal' as const),
    line_item_status: SourcingLineItemCalculationStatusRuntype,
    offer_id_and_signature: OfferIdAndSignatureRuntype,
    cost_summary: CostSummaryRuntype,
    offer: OfferResponseRuntype,
    selection: SolutionSelectionRuntype,
    selected_part: PartLiteRuntype.nullable(),
    selected_part_options: z.array(PartLiteRuntype).nullable(),
    availability: AvailabilityRuntype.nullable(),
    manual_one_time_costs: z.array(SourcingOneTimeCostRuntype).nullable(),
    manual_tco_costs: ManualTcoCostsRuntype.nullable(),
    award_status: AwardStatusRuntype,
});

export const SourcingLineItemCalculationRuntype = z.union([
    ExternallySourcedRuntype,
    InternallyNotSourcedRuntype,
    InternallySourcedRuntype,
]);

export type ExternallySourcedDTO = z.infer<typeof ExternallySourcedRuntype>;
export type InternallyNotSourcedDTO = z.infer<typeof InternallyNotSourcedRuntype>;
export type InternallySourcedDTO = z.infer<typeof InternallySourcedRuntype>;

export type SourcingLineItemCalculationDTO = ExternallySourcedDTO | InternallyNotSourcedDTO | InternallySourcedDTO;

export function isSourcedLineItem(item: SourcingLineItemDTO): item is InternallySourcedDTO {
    return item.sourced_by === 'Internal' && item.line_item_status === SourcingLineItemCalculationStatus.Sourced;
}

export function extractApprovedPartOptions(item: Pick<SourcingLineItemCalculationDTO, 'part_options'>): PartLite[] {
    return item.part_options
        .filter((option) => option.approval_status === ApprovalStatus.Approved)
        .map((option) => option.part);
}

// Backward-compat aliases so existing imports keep working
export type SourcingLineItemDTO = SourcingLineItemCalculationDTO;
export const SourcingLineItemRuntype = SourcingLineItemCalculationRuntype;

export const SourcingLineItemSyncRequestRuntype = z.object({
    options: z.object({
        copy_manual_states: z.boolean(),
        optimize_price_break: z.boolean(),
    }),
    items: z.array(
        z.object({
            origin_sourcing_line_item_id: z.string(),
            target_sourcing_line_item_id: z.string(),
        }),
    ),
});

export type SourcingLineItemSyncRequestDto = z.infer<typeof SourcingLineItemSyncRequestRuntype>;
